import { Icon, Stack, Tooltip, Typography } from '@mui/material';
import { Facility } from '@schooly/api';
import {
  DropdownSelect,
  RoomSelectContent,
  SelectContentSkeleton,
  SelectedRoom,
  SelectSearchInput,
} from '@schooly/components/filters';
import { DROPDOWN } from '@schooly/constants/test-ids';
import { DropdownIcon, InformationIcon } from '@schooly/style';
import { FC, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

interface RoomDropdownSelectProps {
  selectedRoom: SelectedRoom | null;
  facilities: Facility[];
  isLoading: boolean;
  onSelect: (room: SelectedRoom | null) => void;
}

export const RoomDropdownSelect: FC<RoomDropdownSelectProps> = ({
  selectedRoom,
  facilities,
  isLoading,
  onSelect,
}) => {
  const { formatMessage } = useIntl();
  const roomDropdownRef = useRef<DropdownSelect>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  const handleSelect = useCallback(
    (room: SelectedRoom | null) => {
      roomDropdownRef.current?.close();
      onSelect(room);
    },
    [onSelect],
  );

  const renderInput = useCallback(
    (opened: boolean) => {
      if (selectedRoom) {
        return (
          <Typography variant="h3" noWrap>
            {selectedRoom.name}
          </Typography>
        );
      }

      if (opened) {
        return (
          <SelectSearchInput
            ref={inputRef}
            autoFocus
            value={query}
            onChangeText={setQuery}
            debounced={false}
          />
        );
      }

      return null;
    },
    [selectedRoom, query],
  );

  return (
    <DropdownSelect
      ref={roomDropdownRef}
      testId={DROPDOWN.DEFAULT_ROOM}
      placeholder={formatMessage({ id: 'groups-DefaultRoom' })}
      hasValues={!!selectedRoom}
      isLoading={isLoading}
      onClear={selectedRoom ? () => handleSelect(null) : undefined}
      maxExpandedContainerWidth={500}
      onClickInputArea={() => inputRef.current?.focus()}
      sx={{ '.right-icon': { width: 'auto' } }}
      renderRightIcon={(opened) => (
        <Stack direction="row" alignItems="center" gap={1} mr={-0.25}>
          <Tooltip
            title={formatMessage({ id: 'groups-DefaultRoom-Tooltip' })}
            componentsProps={{ tooltip: { sx: { p: 1.25 } } }}
          >
            <Icon
              onClick={(e) => e.stopPropagation()}
              sx={{
                '.svg-icon': {
                  transform: 'none',
                  color: 'common.grey',
                  '&:hover': { color: 'text.primary' },
                },
              }}
            >
              <InformationIcon />
            </Icon>
          </Tooltip>
          <Icon
            sx={{
              '.svg-icon': {
                transform: opened ? 'rotate(180deg)' : 'none',
                color: 'common.grey',
                '&:hover': { color: 'text.primary' },
              },
            }}
          >
            <DropdownIcon />
          </Icon>
        </Stack>
      )}
      renderContent={() =>
        isLoading ? (
          <SelectContentSkeleton />
        ) : (
          <RoomSelectContent
            selectedRoom={selectedRoom}
            facilities={facilities}
            query={query}
            onSelectRoom={handleSelect}
          />
        )
      }
    >
      {renderInput}
    </DropdownSelect>
  );
};
